import React, { Children } from 'react';
import ReactMarkdown from 'react-markdown';
import isEqual from 'lodash/isEqual';
import { ff } from 'helpers/featureFlags';
import DebugRender from 'components/Debug/Render/DebugRender';
import { InputDropdown } from '../InputDropdown/InputDropdown';
import { autotab, ariaLabel } from '../Generic';
import { stateFormErrorConnector } from '../shared/stateFormErrors';
import ValidationElement from '../ValidationElement';

const getSuggestionValue = (suggestion) => suggestion.text;

export const renderSuggestion = (suggestion, search) => {
  let text = `${suggestion.value}`;

  // If the value is different than the name then display that
  // as well
  if (suggestion.text !== suggestion.value) {
    text += ` (${suggestion.text})`;
  }

  // Highlight what was matched
  if (search.query) {
    try {
      const rx = new RegExp(search.query, 'ig');
      if (rx.test(text)) {
        const lastIndex = rx.lastIndex;
        const firstIndex = lastIndex - search.query.length;
        text = `${text.slice(0, lastIndex)}**${text.slice(lastIndex + Math.abs(0))}`;
        text = `${text.slice(0, firstIndex)}**${text.slice(firstIndex + Math.abs(0))}`;
      }
    } catch (e) {
      console.log('Invalid regular expression');
    }
  }

  return <ReactMarkdown>{text}</ReactMarkdown>;
};

class Dropdown extends ValidationElement {
  constructor(props) {
    super(props);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      value: props.value,
      options: [],
      suggestions: [],
      focus: props.focus,
      error: props.error,
      valid: props.valid,
    };

    this.update = this.update.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.onSuggestionChange = this.onSuggestionChange.bind(this);
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.shouldRenderSuggestions = this.shouldRenderSuggestions.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.disallowClipboard = this.disallowClipboard.bind(this);

    this.inputdropdown = React.createRef();
  }

  static getDerivedStateFromProps(props, state) {
    // Handle changing suggestions being passed down by active filtering components
    const suggestionsArr = [];
    if (!isEqual(props.suggestions, state.previousSuggestions)) {
      if (props.suggestions) {
        for (const child of props.suggestions) {
          if (child && child.type === 'option') {
            suggestionsArr.push({
              text: child.props.children || '',
              value: child.props.value,
            });
          }
        }
      }
    }
    if (props.dynamicSuggestions) {
      const dynamicSuggestionsState = { ...state, suggestions: suggestionsArr, previousSuggestions: suggestionsArr };
      return dynamicSuggestionsState;
    }
    const normalState = { ...state };
    return normalState;
  }

  componentDidUpdate(prevProps) {
    const { value, multiple } = this.props;
    if (!multiple && value !== this.state.value) { // fixes data snapshot bug; orig commit 6db74c9
      this.setState({ value }); // eslint-disable-line react/no-did-update-set-state
    }
  }

  componentDidMount() {
    if (this.props.children) {
      const arr = [];
      for (const child of this.props.children) {
        if (child && child.type === 'option') {
          arr.push({
            text: child.props.children || '',
            value: child.props.value,
          });
        }
      }

      this.setState({ options: arr }, () => {
        // Force validation. Particularly on first render we need to revalidate the
        // value.
        const event = {
          target: {
            id: this.props.id,
            name: this.props.name,
            value: this.state.value,
          },
          persist() { },
          fake: true,
        };

        this.handleValidation(event);
      });
    }
  }

  /**
   * This allows us to switch logic of "hasError" between state.application and
   * state.form. When useStateFormErrors is true, we'll use
   * this.props.hasStateFormError. Otherwise, continue to use the previous error
   * handling.
   */
  hasError = () => {
    if (ff(this.props.features).getActive('EN-4733')) {
      return this.props.hasStateFormError;
    }
    return this.state.error;
  };

  /**
   * This allows us to switch logic of "isValid" between state.application and
   * state.form. When useStateFormErrors is true, we'll use
   * this.props.hasStateFormError. Otherwise, continue to use the previous error
   * handling.
   */
  isValid = () => {
    if (ff(this.props.features).getActive('EN-4733')) {
      return !this.props.hasStateFormError && this.state.value?.trim() !== '';
    }
    return this.state.valid;
  };

  errors(value) {
    return (
      this.props.onError(
        value,
        this.constructor.errors.map((err) => ({
          code: err.code,
          valid: err.func(value, {
            ...this.props,
            options: this.state.options,
          }),
          uid: this.state.uid,
        }))
      ) || []
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { value, disabled } = this.props;
    const updates = {};
    if (!isEqual(nextProps.value, value) || nextProps.disabled !== disabled) {
      if (nextProps.value !== this.state.value) {
        updates.value = nextProps.value;
      }

      if (updates.value !== undefined && updates.value !== this.state.value && !nextProps.disabled) {
        const errors = this.errors(updates.value);
        updates.error = errors.some((x) => x.valid === false);
        updates.valid = errors.every((x) => x.valid === true);
      }

      // If disabled, we clear the value and clear state
      if (nextProps.disabled) {
        updates.value = '';
        updates.valid = null;
        updates.error = null;
      }

      this.setState(updates);
    }
  }

  update(queue) {
    const {
      multiple, value, showComments, comments, name
    } = this.props;

    this.props.onUpdate({
      name,
      showComments,
      comments,
      value: multiple ? this.state.value : value,
      ...queue,
    });
  }

  removeFocus() {
    const shouldRemoveFocus = this.props.className === 'relative-relation';
    if (shouldRemoveFocus && this.inputdropdown?.current?.input?.blur) {
      this.inputdropdown.current.input.blur();
    }
  }

  /**
   * Execute validation checks on the value.
   */
  handleValidation(event) {
    const errors = this.errors(this.state.value); // eslint-disable-line react/no-access-state-in-setstate
    this.setState({
      error: errors.some((x) => x.valid === false),
      valid: errors.every((x) => x.valid === true),
    });
  }

  /**
   * Handle the focus event.
   */
  handleFocus(event) {
    this.setState({ focus: true }, () => {
      super.handleFocus(event);
    });
  }

  /**
   * Handle the blur event.
   */
  handleBlur(event) {
    this.setState({ focus: false }, () => {
      if (this.props.onBlur) { this.handleValidation(event); } else { this.update({ value: this.state.value }); }
      super.handleBlur(event);
    });
  }

  /**
   * Handle the key up event.
   */
  handleKeyDown(event) {
    autotab(event, this.props.maxlength, this.props.tabBack, this.props.tabNext);
  }

  /**
   * Prevents clipboard events from making changes to the value of the elements
   */
  disallowClipboard(event) {
    event.preventDefault();
  }

  sortOptions = (options, field) => options.sort((a, b) => {
    if (a[field].toLowerCase() < b[field].toLowerCase()) {
      return -1;
    }

    if (a[field].toLowerCase() > b[field].toLowerCase()) {
      return 1;
    }

    return 0;
  });

  filterInitialOptions = (options, value) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    return options.filter((opt) => opt.value.toLowerCase().slice(0, inputLength) === inputValue);
  };

  filterAdditionalOptions = (options, suggestions) => options.filter((opt) => !suggestions.some((x) => x.text === opt.text));

  getSuggestions(value) {
    // Match first on the value
    let suggestions = [];
    suggestions = this.filterInitialOptions(this.state.options, value);
    if (this.props.sort) {
      suggestions = this.sortOptions(suggestions, 'value');
    }

    // Match second on the text
    let additionalSuggestions = this.filterAdditionalOptions(this.state.options, suggestions);
    if (this.props.sort) {
      additionalSuggestions = this.sortOptions(additionalSuggestions, 'text');
    }

    return suggestions.concat(additionalSuggestions);
  }

  onSuggestionChange(event, change) {
    const {
      multiple, name, beforeChange, onBlur
    } = this.props;

    const option = this.state.options
      .filter((v) => v.text === change.newValue)
      .shift();

    const value = multiple
      ? change.newValue
      : beforeChange(option ? option.value : change.newValue);
    const e = {
      ...event,
      target: {
        id: name,
        name,
        value,
      },
    };

    this.setState({ value }, () => {
      if (onBlur) {
        this.update({ value });
      }
      super.handleChange(e);
    });
  }

  onSuggestionsFetchRequested(query) {
    this.setState({
      suggestions: this.getSuggestions(query.value),
    });
  }

  onSuggestionsClearRequested(value) {
    this.setState({
      suggestions: [],
    });
  }

  onSuggestionSelected(event, options) {
    const value = (options.suggestion || {}).value || this.state.value;
    const future = {
      focus: false,
      suggestions: this.state.suggestions,
      value,
    };

    if (this.props.clearOnSelection) {
      future.suggestions = [];
      future.value = '';
    }

    this.setState(future, () => {
      this.props.onSuggestionSelected(event, options);
      this.handleValidation(event);
      this.props.tabNext();
      this.update({ value });
      this.removeFocus();
    });
  }

  shouldRenderSuggestions(value) {
    if (!this.props.minSuggestionLength || (value?.length || 0) >= this.props.minSuggestionLength) {
      return true;
    }
    return false;
  }

  /**
   * Style classes applied to the wrapper.
   */
  divClass() {
    return `dropdown ${this.props.className || ''} ${!this.props.disabled && this.hasError() ? 'usa-input-error' : ''
    }`.trim();
  }

  /**
   * Style classes applied to the label element.
   */
  labelClass() {
    if (this.props.disabled) {
      return 'disabled';
    }

    return `${this.hasError() ? 'usa-input-error-label' : ''}`.trim();
  }

  /**
   * Style classes applied to the input element.
   */
  inputClass() {
    if (this.props.disabled) {
      return 'disabled';
    }

    if (this.props.multiple) {
      return `${this.state.focus ? 'usa-input-focus' : ''} ${this.isValid() ? 'usa-input-success-default' : ''
      }`.trim();
    }
    return `${this.state.focus ? 'usa-input-focus' : ''} ${this.isValid() ? 'usa-input-success' : ''
    }`.trim();
  }

  render() {
    const {
      multiple, clipboard, onCopy, onCut, onPaste, label
    } = this.props;

    const option = this.state.options
      .filter((v) => [v.text.toLowerCase(), v.value.toLowerCase()].includes(
        this.state.value.toLowerCase()
      ))
      .shift();

    const value = !multiple && option && !this.state.focus
      ? this.props.displayText(option.value, option.text)
      : this.state.value;

    const inputProps = {
      id: this.state.uid,
      value,
      className: this.inputClass(),
      name: this.props.name,
      placeholder: this.props.placeholder,
      disabled: this.props.disabled,
      pattern: this.props.pattern,
      maxLength: this.props.maxlength,
      readOnly: this.props.readonly,
      onChange: this.onSuggestionChange, // eslint-disable-line @typescript-eslint/unbound-method
      onBlur: this.handleBlur, // eslint-disable-line @typescript-eslint/unbound-method
      onFocus: this.handleFocus, // eslint-disable-line @typescript-eslint/unbound-method
      onKeyDown: this.handleKeyDown, // eslint-disable-line @typescript-eslint/unbound-method
      onCopy: clipboard ? onCopy : this.disallowClipboard, // eslint-disable-line @typescript-eslint/unbound-method
      onCut: clipboard ? onCut : this.disallowClipboard, // eslint-disable-line @typescript-eslint/unbound-method
      onPaste: clipboard ? onPaste : this.disallowClipboard, // eslint-disable-line @typescript-eslint/unbound-method
      ariaLabel: this.props.ariaLabel || label || ariaLabel(this.inputdropdown.current),
    };

    return (
      <div className={this.divClass()}>
        <label className={this.labelClass()} htmlFor={this.state.uid}>
          {label}
        </label>
        <DebugRender parent={this} />
        <InputDropdown
          id={this.state.uid}
          suggestions={this.state.suggestions}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested} // eslint-disable-line @typescript-eslint/unbound-method
          onSuggestionsClearRequested={this.onSuggestionsClearRequested} // eslint-disable-line @typescript-eslint/unbound-method
          onSuggestionSelected={this.onSuggestionSelected} // eslint-disable-line @typescript-eslint/unbound-method
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          inputProps={inputProps}
          name={this.props.name}
          highlightFirstSuggestion
          shouldRenderSuggestions={this.shouldRenderSuggestions} // eslint-disable-line @typescript-eslint/unbound-method
          suggestionsRenderProvided={this.props.suggestionsRenderProvided}
          ref={this.inputdropdown}
          hasFullLengthOptions={this.props.hasFullLengthOptions}
        />
      </div>
    );
  }
}

Dropdown.defaultProps = {
  sort: true,
  name: 'dropdown',
  label: '',
  placeholder: '',
  value: '',
  maxlength: 255,
  disabled: false,
  pattern: '',
  readonly: false,
  className: '',
  focus: false,
  error: false,
  valid: false,
  clearOnSelection: false,
  clipboard: true,
  minSuggestionLength: 0,
  suggestions: [],
  dynamicSuggestions: false,
  suggestionsRenderProvided: false,
  hasFullLengthOptions: false,
  tabNext: () => { },
  tabBack: () => { },
  beforeChange: (value) => value,
  displayText: (value, text) => value,
  onSuggestionSelected: (event, options) => { },
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};

Dropdown.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (props.required) {
        return !!value;
      }
      return true;
    },
  },
  {
    code: 'notfound',
    func: (value, props) => {
      if (!value) {
        return null;
      }

      return props.options.some((x) => (
        x.text.toLowerCase() === value.toLowerCase()
        || x.value.toLowerCase() === value.toLowerCase()
      ));
    },
  },
];

/**
 * Dropdown internalTesting. This should only be used for testing and types.
 */
export const internalTesting = {
  Dropdown,
};
export default stateFormErrorConnector(Dropdown);
